import { useState } from 'react';
import { carichi } from '../../api/acquisti';
import type { DettaglioOrdine, RigaNuovoCarico } from '../../api/acquisti';
import { formattaImporto, formattaNumero, oggiIso } from '../../lib/formato';
import { numeroDecimale, numeroDecimaleOZero } from '../../lib/numeri';

interface Props {
  ordine: DettaglioOrdine;
  onAnnulla: () => void;
  onRegistrata: (numero: string | null) => void;
}

interface RigaBolla {
  accettata: string;
  rifiutata: string;
  motivoRifiuto: string;
}

/**
 * Registrare una bolla partendo dall'ordine, e non da una schermata a sé con un elenco
 * di fornitori e ordini da cercare: chi scarica il camion ha in mano il DDT e sa a quale
 * ordine si riferisce, e le righe da spuntare sono quelle ancora aperte di quell'ordine.
 *
 * Le quantità partono da zero e non dal residuo. Un modulo precompilato col residuo si
 * conferma senza guardare, ed è così che una consegna parziale viene registrata come
 * completa — che è il tipo di errore che si scopre quando il materiale non c'è.
 */
export function PannelloCarico({ ordine, onAnnulla, onRegistrata }: Props) {
  const aperte = ordine.righe.filter((r) => r.residuo > 0);

  const [numeroDdt, setNumeroDdt] = useState('');
  const [dataDdt, setDataDdt] = useState(oggiIso());
  const [dataRicevimento, setDataRicevimento] = useState(oggiIso());
  const [note, setNote] = useState('');
  const [righe, setRighe] = useState<Record<string, RigaBolla>>(() =>
    Object.fromEntries(aperte.map((r) => [r.id, { accettata: '', rifiutata: '', motivoRifiuto: '' }])),
  );
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  function cambia(id: string, modifica: Partial<RigaBolla>) {
    setRighe((precedenti) => ({ ...precedenti, [id]: { ...precedenti[id]!, ...modifica } }));
  }

  const conQualcosa = aperte.filter((r) => {
    const b = righe[r.id]!;
    return numeroDecimaleOZero(b.accettata) > 0 || numeroDecimaleOZero(b.rifiutata) > 0;
  });

  async function registra() {
    if (!numeroDdt.trim()) {
      setErrore('Serve il numero della bolla.');
      return;
    }

    const daInviare: RigaNuovoCarico[] = [];

    for (const riga of aperte) {
      const bolla = righe[riga.id]!;
      // Un campo vuoto e uno zero scritto a mano vanno distinti: il primo è una riga
      // che nessuno ha compilato, il secondo è «di questa non è arrivato niente».
      const accettata = numeroDecimale(bolla.accettata) ?? 0;
      const rifiutata = numeroDecimale(bolla.rifiutata) ?? 0;

      if (accettata === 0 && rifiutata === 0) continue;

      if (accettata < 0 || rifiutata < 0) {
        setErrore(`«${riga.descrizione}»: le quantità non possono essere negative.`);
        return;
      }

      // Lo rifiuta anche il dominio, ma dirlo qui evita un giro di rete per scoprire
      // una cosa che si vede guardando la riga.
      if (rifiutata > 0 && !bolla.motivoRifiuto.trim()) {
        setErrore(`«${riga.descrizione}»: serve il motivo del rifiuto, è quello che si scrive al fornitore.`);
        return;
      }

      daInviare.push({
        rigaOrdineFornitoreId: riga.id,
        accettata,
        rifiutata,
        motivoRifiuto: bolla.motivoRifiuto.trim() || null,
      });
    }

    if (daInviare.length === 0) {
      setErrore('Non è stata segnata nessuna quantità: una bolla senza righe non è una bolla.');
      return;
    }

    setInCorso(true);
    setErrore(null);
    try {
      const carico = await carichi.registra({
        codiceFornitoreMago: ordine.codiceFornitoreMago,
        numeroDdt: numeroDdt.trim(),
        dataDdt,
        dataRicevimento,
        righe: daInviare,
        note: note.trim() || null,
      });
      onRegistrata(carico.numero);
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setInCorso(false);
    }
  }

  return (
    <div className="modulo-scheda bolla">
      <h3>Registra una bolla</h3>
      <p className="nota">
        Le quantità partono da zero: si scrive quello che è arrivato davvero. Quello che
        arriva e viene respinto non conta come ricevuto, e il residuo resta aperto.
      </p>

      <div className="campi">
        <label>
          Numero bolla
          <input
            type="text"
            required
            value={numeroDdt}
            onChange={(e) => setNumeroDdt(e.target.value)}
          />
        </label>
        <label>
          Data bolla
          <input type="date" value={dataDdt} onChange={(e) => setDataDdt(e.target.value)} />
        </label>
        <label>
          Ricevuta il
          <input
            type="date"
            value={dataRicevimento}
            onChange={(e) => setDataRicevimento(e.target.value)}
          />
        </label>
      </div>

      <ul className="righe-bolla">
        {aperte.map((riga) => {
          const bolla = righe[riga.id]!;
          const respinge = numeroDecimaleOZero(bolla.rifiutata) > 0;

          return (
            <li key={riga.id}>
              <div className="riga-cosa">
                <p className="descrizione">{riga.descrizione}</p>
                <p className="riga-provenienza">
                  {riga.codiceArticoloFornitore && <span>cod. {riga.codiceArticoloFornitore}</span>}
                  <span>
                    da ricevere {formattaNumero(riga.residuo)} {riga.unitaMisura} su{' '}
                    {formattaNumero(riga.quantita)}
                  </span>
                  <span>{formattaImporto(riga.costoNetto)}</span>
                </p>
              </div>

              <div className="quantita-bolla">
                <label>
                  Accettata
                  <input
                    type="text"
                    inputMode="decimal"
                    value={bolla.accettata}
                    onChange={(e) => cambia(riga.id, { accettata: e.target.value })}
                  />
                </label>
                <button
                  type="button"
                  className="tutto"
                  title="Tutto quello che restava da ricevere"
                  onClick={() => cambia(riga.id, { accettata: String(riga.residuo) })}
                >
                  tutto
                </button>
                <label>
                  Respinta
                  <input
                    type="text"
                    inputMode="decimal"
                    value={bolla.rifiutata}
                    onChange={(e) => cambia(riga.id, { rifiutata: e.target.value })}
                  />
                </label>
              </div>

              {respinge && (
                <label className="motivo-rifiuto">
                  Perché è stata respinta
                  <input
                    type="text"
                    value={bolla.motivoRifiuto}
                    placeholder="quello che si scrive al fornitore per farsi rimandare la merce"
                    onChange={(e) => cambia(riga.id, { motivoRifiuto: e.target.value })}
                  />
                </label>
              )}
            </li>
          );
        })}
      </ul>

      <label className="larga">
        Note sulla bolla
        <input type="text" value={note} onChange={(e) => setNote(e.target.value)} />
      </label>

      {errore && <p className="errore">{errore}</p>}

      <div className="azioni">
        <span className="conteggio">
          {conQualcosa.length === 0
            ? 'Nessuna riga segnata'
            : conQualcosa.length === 1
              ? '1 riga sulla bolla'
              : `${conQualcosa.length} righe sulla bolla`}
        </span>
        <button type="button" disabled={inCorso} onClick={onAnnulla}>
          Lascia stare
        </button>
        <button
          type="button"
          className="primario"
          disabled={inCorso || conQualcosa.length === 0 || !numeroDdt.trim()}
          onClick={() => void registra()}
        >
          {inCorso ? 'Registro…' : 'Registra la bolla'}
        </button>
      </div>
    </div>
  );
}

